import './styles/globals.css';
import './app-pages/pages/messaging/chat/Chat.css';
import './app-pages/pages/consult/results/ResultsBar.css';
import './styles/CalendarMobile.css';

import React, { lazy, Suspense } from 'react';
import { ThemeProvider } from '@emotion/react';
import { AppErrorBoundary } from 'app-pages/error/AppErrorBoundary';
import ErrorPage from 'app-pages/error/ErrorPage';
import { HomeLayout } from 'app-pages/pages/_sharedLayouts/HomeLayout';
import AcceptInviteSuccessPage from 'app-pages/pages/account/AcceptInviteSuccessPage';
import SessionErrorPage from 'app-pages/pages/account/SessionErrorPage';
import { ConsultLayout } from 'app-pages/pages/consult/components/ConsultLayout';
import { DevelopmentPage } from 'app-pages/pages/development/DevelopmentPage';
import IndexPage from 'app-pages/pages/IndexPage';
import TransperfectLanguageSelector from 'components/_temp_ui_common/Integrations/TransperfectLanguageSelector';
import { AgreementCheck } from 'components/agreement/AgreementCheck';
import {
  AuthProviderWithRedirectCallback,
  ProtectedRoute,
} from 'components/auth0/AuthProvider';
import { theme } from 'components/core/theme';
import { IdleWarningModal } from 'components/idle-warning-modal/IdleWarningModal';
import { InternationalizationProvider } from 'components/intl/InternationalizationProvider';
import { AccountPatientsProvider } from 'contexts/AccountPatientsContext';
import { BrowserProvider } from 'contexts/BrowserContext';
import { ChatProvider } from 'contexts/ChatContext';
import { CurrentUserProvider } from 'contexts/CurrentUserContext';
import { SponsorsProvider } from 'contexts/SponsorsContext';
import { FeatureFlags, useFlag } from 'hooks/useFlag';
import { useScrollOnNavigation } from 'hooks/useScrollOnNavigation';
import { HelmetProvider } from 'react-helmet-async';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';

/* eslint-disable prettier/prettier */
const AcceptInvitePage = lazy(
  () => import('app-pages/pages/account/AcceptInvitePage'),
);
const LeadsRouter = lazy(() => import('app-pages/pages/leads/LeadsRouter'));
const UpdateChildPage = lazy(
  () => import('app-pages/pages/account/UpdateChild'),
);
const UpdateMemberPCPPage = lazy(
  () => import('app-pages/pages/account/UpdateMemberPCP'),
);
const ExclusionComponent = lazy(
  () => import('app-pages/pages/account/ExclusionComponent'),
);
const ExclusionConfirmComponent = lazy(
  () => import('app-pages/pages/account/ExclusionConfirmComponent'),
);
const RelationshipPageComponent = lazy(
  () => import('app-pages/pages/account/RelationshipPageComponent'),
);
const AccountCreatePage = lazy(
  () => import('app-pages/pages/login/AccountCreatePage'),
);
const HomePageComponent = lazy(
  () => import('app-pages/pages/home/HomePageComponent'),
);
const AccountSettingsPage = lazy(
  () => import('app-pages/pages/settings/AccountSettingsPage'),
);
const AccountAndMemberSettingsPage = lazy(
  () => import('app-pages/pages/settings/AccountAndMemberSettingsPage'),
);
const PreferencesSettingsPage = lazy(
  () => import('app-pages/pages/settings/PreferencesSettingsPage'),
);
const AccountUpdatePage = lazy(
  () => import('app-pages/pages/settings/AccountUpdatePage'),
);
const PatientSettingsPage = lazy(
  () => import('app-pages/pages/settings/PatientSettingsPage'),
);
const PlansComponent = lazy(
  () => import('app-pages/pages/plans/PlansComponent'),
);
const SettingsPage = lazy(
  () => import('app-pages/pages/settings/SettingsPage'),
);
const StartConsultPage = lazy(
  () => import('app-pages/pages/consult/ConsultStartPage'),
);
const AssessmentWelcomePage = lazy(
  () => import('app-pages/pages/consult/AssessmentWelcomePage'),
);
const MedicalHistorySurveyPage = lazy(
  () =>
    import('app-pages/pages/consult/medical-history/MedicalHistorySurveyPage'),
);
const DemographicsReviewPage = lazy(
  () => import('app-pages/pages/consult/demographics/DemographicsReviewPage'),
);
const PatientVitalsPageComponent = lazy(
  () => import('app-pages/pages/consult/PatientVitalsPageComponent'),
);

const ConsultVideoPage = lazy(
  () => import('app-pages/pages/consult/video/ConsultVideoPage'),
);
const ConsultSurveyPage = lazy(
  () => import('app-pages/pages/consult/survey/ConsultSurveyPage'),
);
const MessagingPage = lazy(
  () => import('app-pages/pages/messaging/MessagingPage'),
);
const ChatChannelPage = lazy(
  () => import('app-pages/pages/messaging/chat/ChatChannelPage'),
);
const ConsultResultsPage = lazy(
  () => import('app-pages/pages/consult/results/ConsultResultsPage'),
);
const NotThereYet = lazy(() => import('app-pages/pages/consult/NotThere'));

const SubscribePage = lazy(
  () => import('app-pages/pages/consult/SubscribePage'),
);
const StripeSuccessPage = lazy(
  () => import('app-pages/pages/consult/StripeSuccessPage'),
);
const StripeSuccessPageC3HS = lazy(
  () => import('app-pages/pages/consult/StripeSuccessPageC3HS'),
);
const EligibilityCheckPage = lazy(
  () => import('app-pages/pages/consult/EligibilityCheckPage'),
);

const ScheduleFirstAppointmentPage = lazy(
  () => import('app-pages/pages/consult/ScheduleFirstAppointmentPage'),
);
const PlanPage = lazy(() => import('app-pages/pages/plans/PlanPage'));
const WelcomeScreen = lazy(
  () => import('app-pages/pages/consult/eligibility/WelcomeScreen'),
);
const AccountVerifyPage = lazy(
  () => import('app-pages/pages/account/VerifyPage'),
);
const HistoryReview = lazy(
  () => import('app-pages/pages/consult/HistoryReviewPage'),
);
const WelcomePage = lazy(() => import('app-pages/pages/welcome/WelcomePage'));
const NoShowCancellationPolicy = lazy(
  () => import('app-pages/pages/consult/eligibility/NoShowCancellationPolicy'),
);
const ConsultCancellationPage = lazy(
  () =>
    import(
      'app-pages/pages/consult/eligibility/ginaConsent/ConsultCancellationPage'
    ),
);
const CommunityPage = lazy(
  () => import('app-pages/pages/community/CommunityPage'),
);
const AppointmentsPage = lazy(
  () => import('app-pages/pages/appointments/AppointmentsPage'),
);

const AgreementsPage = lazy(
  () => import('app-pages/pages/consult/agreements/AgreementsPage'),
);

const AspIntakeWelcomePage = lazy(
  () => import('app-pages/pages/asp/intake/AspIntakeWelcomePage'),
);

const AspIntakeLeadInfoPage = lazy(
  () => import('app-pages/pages/asp/intake/AspIntakeLeadInfoPage'),
);

const AspIntakeInsurancePage = lazy(
  () => import('app-pages/pages/asp/intake/AspIntakeInsurancePage'),
);

const AspIntakeAgreementsPage = lazy(
  () => import('app-pages/pages/asp/intake/AspIntakeAgreementsPage'),
);

const AspIntakeFinRespPage = lazy(
  () => import('app-pages/pages/asp/intake/AspIntakeFinRespPage'),
);

const AspIntakeSuccessPage = lazy(
  () => import('app-pages/pages/asp/intake/AspIntakeSuccessPage'),
);

const AgreementsRenewalPage = lazy(
  () => import('app-pages/pages/consult/agreements/AgreementsRenewalPage'),
);

const DocumentsPage = lazy(
  () => import('app-pages/pages/settings/DocumentsPage'),
);

const TestStylesPage = lazy(() => import('app-pages/pages/StylesTestPage'));

function fromSubdomain(subdomain: string): boolean {
  const hostname = window.location.hostname;
  const currentSubdomain = hostname.split('.')[0];
  return currentSubdomain.startsWith(subdomain);
}

const queryClient = new QueryClient({});

export function App() {
  const aspLeadIntakeEnabled = useFlag(FeatureFlags.AspLeadIntakeEnabled, true);
  const displayInformedConsents = useFlag(
    FeatureFlags.DisplayInformedConsents,
    true,
  );

  const transperfectLanguageSelectorEnabled = useFlag(
    FeatureFlags.TransperfectLanguageSelectorEnabled,
  );
  useScrollOnNavigation();

  let requireSession = true;
  if (requireSession && AUTH_BYPASS && global.location.pathname === '/login') {
    requireSession = false;
  }

  const fromAspSubdomain = fromSubdomain('asp');
  const fromNPSubdomain = fromSubdomain('np');
  const fromReferSubdomain = fromSubdomain('refer');

  const fromMainDomain =
    !fromReferSubdomain && !fromAspSubdomain && !fromNPSubdomain;

  return (
    <BrowserProvider>
      <HelmetProvider>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            {!fromMainDomain && (
              <LeadsRouter
                fromAspSubdomain={fromAspSubdomain}
                fromNPSubdomain={fromNPSubdomain}
                fromReferSubdomain={fromReferSubdomain}
              />
            )}

            <Routes>
              {/**
               * Unauthenticated routes will be siblings to this wildcard route
               */}
              <Route path="/session-error" element={<SessionErrorPage />} />
              {fromMainDomain && (
                <Route
                  path="/*"
                  element={
                    <AuthProviderWithRedirectCallback>
                      {transperfectLanguageSelectorEnabled && (
                        <TransperfectLanguageSelector />
                      )}
                      <AppErrorBoundary>
                        <InternationalizationProvider>
                          <CurrentUserProvider requiresSession={requireSession}>
                            <SponsorsProvider>
                              <ChatProvider>
                                <AgreementCheck>
                                  <Routes>
                                    {AUTH_BYPASS && (
                                      <Route
                                        path="/login"
                                        element={<DevelopmentPage />}
                                      />
                                    )}
                                    {REACT_APP_STAGE !== 'production' && (
                                      <Route
                                        path="/test"
                                        element={
                                          <Suspense>
                                            <TestStylesPage />
                                          </Suspense>
                                        }
                                      />
                                    )}
                                    <Route
                                      path="error"
                                      element={
                                        <Suspense>
                                          <ErrorPage />
                                        </Suspense>
                                      }
                                    />
                                    <Route
                                      path="/accept-invite"
                                      element={
                                        <Suspense>
                                          <AcceptInvitePage />
                                        </Suspense>
                                      }
                                    />
                                    <Route
                                      path="/accept-invite/success"
                                      element={
                                        <Suspense>
                                          <AcceptInviteSuccessPage />
                                        </Suspense>
                                      }
                                    />
                                    <Route
                                      index
                                      element={
                                        <Suspense>
                                          <ProtectedRoute
                                            component={IndexPage}
                                          />
                                        </Suspense>
                                      }
                                    />
                                    <Route
                                      path="account/create"
                                      element={
                                        <Suspense>
                                          <ProtectedRoute
                                            component={AccountCreatePage}
                                          />
                                        </Suspense>
                                      }
                                    />
                                    {/* lead onboarding */}
                                    {aspLeadIntakeEnabled && (
                                      <Route path="/asp/intake/:id">
                                        <Route
                                          path="welcome"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={AspIntakeWelcomePage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="info"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={
                                                  AspIntakeLeadInfoPage
                                                }
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="insurance"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={
                                                  AspIntakeInsurancePage
                                                }
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="agreements"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={
                                                  AspIntakeAgreementsPage
                                                }
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="financial-responsibility"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={AspIntakeFinRespPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="success"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={AspIntakeSuccessPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                      </Route>
                                    )}

                                    {/* dashboard and related pages */}

                                    <Route
                                      element={
                                        <AccountPatientsProvider>
                                          <Outlet />
                                        </AccountPatientsProvider>
                                      }
                                    >
                                      {/* home pages- with navigtation */}
                                      <Route element={<HomeLayout />}>
                                        <Route
                                          path="home"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={HomePageComponent}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="/appointments/:memberId?/:appointmentId?"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={AppointmentsPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="account/update"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={AccountUpdatePage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="account/update-profile"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={
                                                  AccountAndMemberSettingsPage
                                                }
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="account/verify"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={AccountVerifyPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="settings"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={SettingsPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="plans"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={PlansComponent}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="plans/:selectedPatientId"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={PlansComponent}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="settings/account"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={AccountSettingsPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="settings/preferences"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={
                                                  PreferencesSettingsPage
                                                }
                                              />
                                            </Suspense>
                                          }
                                        />
                                        {displayInformedConsents && (
                                          <Route
                                            path="settings/documents/:selectedPatientId?"
                                            element={
                                              <Suspense>
                                                <ProtectedRoute
                                                  component={DocumentsPage}
                                                />
                                              </Suspense>
                                            }
                                          />
                                        )}
                                        <Route
                                          path="settings/:patientId"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={PatientSettingsPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="messaging"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={MessagingPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="messaging/:chatId"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={ChatChannelPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="/consult/:consultId/treatment-plan"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={PlanPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="messaging"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={MessagingPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="messaging/:chatId"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={ChatChannelPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="/consult/:consultId/treatment-plan"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={PlanPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="/community"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={CommunityPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                      </Route>

                                      {/* home pages - no navigation */}
                                      <Route element={<HomeLayout hideNav />}>
                                        <Route
                                          path="account/relationship"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={
                                                  RelationshipPageComponent
                                                }
                                              />
                                            </Suspense>
                                          }
                                        />

                                        {/* TODO: remove add-child or implement some sort of redirect component */}
                                        <Route
                                          path="account/add-child"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={UpdateChildPage}
                                              />
                                            </Suspense>
                                          }
                                        />

                                        <Route
                                          path="account/add-member"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={UpdateChildPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="account/update-child/:patientId"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={UpdateChildPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="account/update-member/:patientId"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={UpdateChildPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="account/update-member-pcp/:patientId"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={UpdateMemberPCPPage}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="account/exclusion"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={ExclusionComponent}
                                              />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="account/exclusion-confirm"
                                          element={
                                            <Suspense>
                                              <ProtectedRoute
                                                component={
                                                  ExclusionConfirmComponent
                                                }
                                              />
                                            </Suspense>
                                          }
                                        />
                                      </Route>
                                    </Route>

                                    {/* Agreement page - the consult ID is just a passthrough */}
                                    <Route
                                      path="member/:memberId/agreements/:consultId?"
                                      element={
                                        <Suspense>
                                          <ProtectedRoute
                                            component={AgreementsPage}
                                          />
                                        </Suspense>
                                      }
                                    />
                                    <Route
                                      path="agreements/renewal"
                                      element={
                                        <Suspense>
                                          <ProtectedRoute
                                            component={AgreementsRenewalPage}
                                          />
                                        </Suspense>
                                      }
                                    />

                                    {/* Consult pages */}
                                    <Route
                                      path="consult/:consultId"
                                      element={<ConsultLayout />}
                                    >
                                      <Route
                                        path="start"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={StartConsultPage}
                                            />
                                          </Suspense>
                                        }
                                      />
                                      <Route
                                        path="welcome"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={AssessmentWelcomePage}
                                            />
                                          </Suspense>
                                        }
                                      />
                                      <Route
                                        path="demographics-review"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={DemographicsReviewPage}
                                            />
                                          </Suspense>
                                        }
                                      />
                                      <Route
                                        path="survey/:surveyId"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={ConsultSurveyPage}
                                            />
                                          </Suspense>
                                        }
                                      />

                                      <Route
                                        path="patient-vitals"
                                        element={
                                          <Suspense>
                                            <PatientVitalsPageComponent />
                                          </Suspense>
                                        }
                                      />
                                      <Route
                                        path="history"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={
                                                MedicalHistorySurveyPage
                                              }
                                            />
                                          </Suspense>
                                        }
                                      />
                                      <Route
                                        path="results"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={ConsultResultsPage}
                                            />
                                          </Suspense>
                                        }
                                      />
                                      <Route
                                        path="subscription"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={SubscribePage}
                                            />
                                          </Suspense>
                                        }
                                      />
                                      <Route
                                        path="stripe_cancel"
                                        element={
                                          <Navigate
                                            to="../subscription"
                                            replace
                                          />
                                        }
                                      />
                                      <Route
                                        path="stripe_success"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={StripeSuccessPage}
                                            />
                                          </Suspense>
                                        }
                                      />
                                      <Route
                                        path="stripe_success_c3hs"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={StripeSuccessPageC3HS}
                                            />
                                          </Suspense>
                                        }
                                      />
                                      <Route
                                        path="schedule/first-appointment"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={
                                                ScheduleFirstAppointmentPage
                                              }
                                            />
                                          </Suspense>
                                        }
                                      />
                                      <Route
                                        path="videos"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={ConsultVideoPage}
                                            />
                                          </Suspense>
                                        }
                                      />
                                      <Route
                                        path="not-there-yet"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={NotThereYet}
                                            />
                                          </Suspense>
                                        }
                                      />
                                      <Route
                                        path="history-review"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={HistoryReview}
                                            />
                                          </Suspense>
                                        }
                                      />
                                    </Route>

                                    {/* eligibility pages */}
                                    <Route
                                      path="eligibility/:consultId"
                                      element={<ConsultLayout />}
                                    >
                                      <Route
                                        path="check"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={EligibilityCheckPage}
                                            />
                                          </Suspense>
                                        }
                                      />
                                      <Route
                                        path="welcome"
                                        element={
                                          <Suspense>
                                            <ProtectedRoute
                                              component={WelcomeScreen}
                                            />
                                          </Suspense>
                                        }
                                      />

                                      <Route
                                        path="no-show-and-cancellation-policy"
                                        element={
                                          <Suspense>
                                            <NoShowCancellationPolicy />
                                          </Suspense>
                                        }
                                      />
                                    </Route>

                                    <Route
                                      path="/welcome"
                                      element={
                                        <Suspense>
                                          <WelcomePage />
                                        </Suspense>
                                      }
                                    />

                                    <Route
                                      path="/welcome"
                                      element={
                                        <Suspense>
                                          <WelcomePage />
                                        </Suspense>
                                      }
                                    />

                                    <Route
                                      path="consult/:consultId/consult-cancellation"
                                      element={
                                        <Suspense>
                                          <ConsultCancellationPage />
                                        </Suspense>
                                      }
                                    />
                                    <Route
                                      path="*"
                                      element={
                                        <Suspense>
                                          <ProtectedRoute
                                            component={IndexPage}
                                          />
                                        </Suspense>
                                      }
                                    />
                                  </Routes>
                                  <IdleWarningModal />
                                </AgreementCheck>
                              </ChatProvider>
                            </SponsorsProvider>
                          </CurrentUserProvider>
                        </InternationalizationProvider>
                      </AppErrorBoundary>
                    </AuthProviderWithRedirectCallback>
                  }
                />
              )}
            </Routes>
          </QueryClientProvider>
        </ThemeProvider>
      </HelmetProvider>
    </BrowserProvider>
  );
}
